import { f7, useStore } from 'framework7-react';
import { FC, useEffect, useState } from 'react';
import {
  getSubscriptionStatusApi,
  postSubscriptionRegisterVehicleApi,
} from '@/api/subscription';
import { getValidWarranties } from '@/components/pages/WarrantyManagement/WarrantyManagementHelper';
import { PageTemplate } from '@/components/uiParts/Template/PageTemplate/PageTemplate';
import { initialState } from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { Car } from '@/types/api/customerApi';
import { storeDispatch } from '@/utils/store';

interface SubscriptionCompletePageProps {
  subscriptionId?: number;
  planName?: string;
  monthlyFee?: number;
  hasRegisteredCar?: boolean;
  selectedMakerName?: string;
  selectedCarName?: string;
  tStockCarId?: number;
}

export const SubscriptionCompletePage: FC<SubscriptionCompletePageProps> = ({
  subscriptionId,
  planName: propPlanName,
  monthlyFee,
  hasRegisteredCar: propHasRegisteredCar,
  selectedMakerName,
  selectedCarName,
  tStockCarId: propTStockCarId,
}) => {
  const authInfo = useStore(store, 'authInfo') as any;
  const ownedCars = useStore(store, 'carsInfo') as Car[];
  const [hasRegisteredCar, setHasRegisteredCar] = useState(false);
  const [hasPaymentDone, setHasPaymentDone] = useState(false);
  const [planName, setPlanName] = useState(propPlanName || '');
  const [resolvedSubId, setResolvedSubId] = useState<number | null>(null);
  const [subStatus, setSubStatus] = useState<number | null>(null);
  const [pendingCarLabel, setPendingCarLabel] = useState(
    [selectedMakerName, selectedCarName].filter(Boolean).join(' '),
  );
  const [isLinking, setIsLinking] = useState(false);
  const effectiveSubId = subscriptionId ?? resolvedSubId ?? null;
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [statusError, setStatusError] = useState(false);
  const [autoLinkAttempted, setAutoLinkAttempted] = useState(false);
  const [autoLinkFailed, setAutoLinkFailed] = useState(false);

  // props 由来 or localStorage 復元値（離脱→再開で選択済み車両を引き継ぐ）
  // お知らせ経由では propTStockCarId が未設定のため、初回レンダリングから
  // localStorage を同期的に読み取り表示差分を防ぐ
  const [tStockCarId, setTStockCarId] = useState<number | undefined>(() => {
    if (propTStockCarId) return propTStockCarId;
    const cid = authInfo?.m_customer_id;
    if (!cid) return undefined;
    try {
      const raw = window.localStorage.getItem('subscription_t_stock_car_id');
      if (!raw) return undefined;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.tStockCarId !== 'number') return undefined;
      if (parsed.cid && parsed.cid !== cid) return undefined;
      if (parsed.sid && subscriptionId && parsed.sid !== subscriptionId)
        return undefined;
      return parsed.tStockCarId;
    } catch {
      return undefined;
    }
  });

  // サブスク契約状態の取得
  useEffect(() => {
    const fetchStatus = async (retry = 1): Promise<void> => {
      try {
        const res = await getSubscriptionStatusApi(
          authInfo.m_customer_id,
          null,
          effectiveSubId,
        );
        if (!res.data.success) {
          if (retry > 0) return fetchStatus(retry - 1);
          setStatusError(true);
          setStatusLoaded(true);
          return;
        }
        if (!res.data.data?.subscription) {
          // subscription=null: has_subscription=false（契約なし）または反映遅延
          // subscriptionId props あり、または has_subscription=true なら遅延の可能性 → リトライ
          const mayBeDelayed =
            subscriptionId || res.data.data?.has_subscription;
          if (mayBeDelayed && retry > 0) return fetchStatus(retry - 1);
          setStatusError(!!mayBeDelayed);
          setStatusLoaded(true);
          return;
        }
        const sub = res.data.data.subscription;
        if (!propPlanName && sub.plan_name) setPlanName(sub.plan_name);
        if (sub.t_subscription_id) setResolvedSubId(sub.t_subscription_id);
        setSubStatus(sub.status);
        const vehicleLabel = [sub.maker_name, sub.car_name]
          .filter(Boolean)
          .join(' ');
        if (vehicleLabel) setPendingCarLabel(vehicleLabel);
        if (sub.apply_status === 0) setHasPaymentDone(true);
        if (sub.t_stock_car_id) setHasRegisteredCar(true);
        setStatusLoaded(true);
      } catch (e) {
        if (retry > 0) return fetchStatus(retry - 1);
        console.error(e);
        setStatusError(true);
        setStatusLoaded(true);
      }
    };
    if (authInfo?.m_customer_id) fetchStatus();
  }, [authInfo?.m_customer_id, effectiveSubId]);

  // tStockCarId を localStorage に永続化（離脱→再開時に PaymentPage で復元するため）
  useEffect(() => {
    if (!tStockCarId || !effectiveSubId || !authInfo?.m_customer_id) return;
    window.localStorage.setItem(
      'subscription_t_stock_car_id',
      JSON.stringify({
        cid: authInfo.m_customer_id,
        sid: effectiveSubId,
        tStockCarId,
      }),
    );
  }, [authInfo?.m_customer_id, effectiveSubId, tStockCarId]);

  // localStorage から tStockCarId を復元（離脱→再開で選択済み車両を引き継ぐ）
  useEffect(() => {
    if (tStockCarId) return;
    const cid = authInfo?.m_customer_id;
    if (!cid) return;
    try {
      const raw = window.localStorage.getItem('subscription_t_stock_car_id');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.tStockCarId !== 'number') {
        window.localStorage.removeItem('subscription_t_stock_car_id');
        return;
      }
      if (parsed.cid && parsed.cid !== cid) {
        window.localStorage.removeItem('subscription_t_stock_car_id');
        return;
      }
      // sid が保存されているが effectiveSubId 未確定 → 検証不能なので待機
      if (parsed.sid && parsed.sid !== 0 && !effectiveSubId) return;
      if (parsed.sid && effectiveSubId && parsed.sid !== effectiveSubId) {
        window.localStorage.removeItem('subscription_t_stock_car_id');
        return;
      }
      setTStockCarId(parsed.tStockCarId);
    } catch {
      window.localStorage.removeItem('subscription_t_stock_car_id');
    }
  }, [authInfo?.m_customer_id, effectiveSubId, tStockCarId]);

  // 既存車選択時: ステータス取得後に自動で registerVehicle を呼ぶ
  useEffect(() => {
    if (
      !tStockCarId ||
      !effectiveSubId ||
      !statusLoaded ||
      subStatus !== 2 ||
      !hasPaymentDone ||
      hasRegisteredCar ||
      isLinking ||
      autoLinkAttempted
    )
      return;
    setAutoLinkAttempted(true);
    setIsLinking(true);
    (async () => {
      try {
        const res = await postSubscriptionRegisterVehicleApi({
          t_subscription_id: effectiveSubId,
          t_stock_car_id: tStockCarId,
        });
        if (!res.data?.success) {
          throw new Error(
            res.data?.error_message || '車両の紐付けに失敗しました',
          );
        }
        setHasRegisteredCar(true);
        window.localStorage.removeItem('subscription_t_stock_car_id');
        setTStockCarId(undefined);
        // ステータス再取得して画面を最新化
        const statusRes = await getSubscriptionStatusApi(
          authInfo.m_customer_id,
        );
        if (statusRes.data.success && statusRes.data.data?.subscription) {
          const sub = statusRes.data.data.subscription;
          setSubStatus(sub.status);
          if (sub.apply_status === 0) setHasPaymentDone(true);
        }
      } catch (e: any) {
        console.error('auto registerVehicle failed', e);
        setAutoLinkFailed(true);
      } finally {
        setIsLinking(false);
      }
    })();
  }, [
    tStockCarId,
    effectiveSubId,
    statusLoaded,
    subStatus,
    hasPaymentDone,
    hasRegisteredCar,
    isLinking,
    autoLinkAttempted,
  ]);

  // 車両を手動で紐付ける（status=2 かつ支払い完了済みの場合のみ許可）
  const handleLinkCar = async (car: Car) => {
    if (isLinking || !effectiveSubId || subStatus !== 2 || !hasPaymentDone)
      return;
    const stockCarId = car.t_stock_car_id;
    if (!stockCarId || warrantyCarIds.has(stockCarId)) return;
    setIsLinking(true);
    try {
      const res = await postSubscriptionRegisterVehicleApi({
        t_subscription_id: effectiveSubId,
        t_stock_car_id: stockCarId,
      });
      if (!res.data?.success) {
        throw new Error(
          res.data?.error_message || '車両の紐付けに失敗しました',
        );
      }
      setHasRegisteredCar(true);
      window.localStorage.removeItem('subscription_t_stock_car_id');
      setTStockCarId(undefined);
    } catch (e: any) {
      f7.dialog.alert(e.message || '車両の紐付けに失敗しました');
    } finally {
      setIsLinking(false);
    }
  };

  const handleClickPayment = () => {
    f7.view.main.router.navigate(paths.subscriptionPayment, {
      props: {
        subscriptionId: effectiveSubId ?? undefined,
        planName,
        monthlyFee,
        tStockCarId,
      },
    });
  };

  const handleClickHome = () => {
    f7.view.main.router.navigate(paths.home);
  };

  // 保証加入済み車両を除外（各車両の warranties から判定）
  const warrantyCarIds = new Set(
    (ownedCars || [])
      .filter((car) => {
        const carWarranties = getValidWarranties(car.warranties || []);
        return carWarranties.length > 0;
      })
      .map((car) => car.t_stock_car_id)
      .filter(Boolean),
  );
  const selectableCars = (ownedCars || []).filter(
    (car) => !!car.t_stock_car_id && !warrantyCarIds.has(car.t_stock_car_id),
  );
  // 車両未紐付け かつ effectiveSubId 取得済み かつ status=2（車検証待ち）かつ 支払い完了済み かつ 候補車両あり
  // auto-link 対象の tStockCarId がある間は手動選択UIを出さない（競合防止）
  // auto-link 失敗時のみフォールバックとして表示する
  const showCarSelect =
    !hasRegisteredCar &&
    !!effectiveSubId &&
    subStatus === 2 &&
    hasPaymentDone &&
    (!tStockCarId || autoLinkFailed) &&
    selectableCars.length > 0;

  return (
    <PageTemplate
      pageName="subscription-complete"
      title={planName || 'サブスクリプション'}
      showNavBack
    >
      <div style={{ padding: '32px 16px', textAlign: 'center' }}>
        {/* 完了バッジ */}
        <div
          style={{
            display: 'inline-block',
            padding: '6px 16px',
            backgroundColor: '#E3F2FD',
            borderRadius: '20px',
            fontSize: '13px',
            color: '#1E55E6',
            fontWeight: 'bold',
            marginBottom: '16px',
          }}
        >
          お申し込み完了！
        </div>

        <div
          style={{
            width: '48px',
            height: '48px',
            margin: '0 auto 16px',
            borderRadius: '50%',
            backgroundColor: '#E8F5E9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            color: '#4CAF50',
          }}
        >
          ✓
        </div>

        <h2
          style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '24px' }}
        >
          {planName || 'サブスクリプションサービス'}の<br />
          お申し込みありがとうございます！
        </h2>

        {/* ステータス */}
        <div
          style={{
            padding: '20px',
            margin: '0 0 24px',
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: '1px solid #e0e0e0',
            textAlign: 'left',
          }}
        >
          <div
            style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}
          >
            {tStockCarId
              ? 'サービス開始にはお支払いが必要です'
              : 'サービス開始にはお支払いと車両の本登録が必要です'}
          </div>
          {!hasRegisteredCar && pendingCarLabel && (
            <div
              style={{
                padding: '12px',
                marginBottom: '12px',
                borderRadius: '8px',
                backgroundColor: '#F6F8FF',
                border: '1px solid #E0E7FF',
              }}
            >
              <div
                style={{
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '4px',
                }}
              >
                登録待ちの車両
              </div>
              <div style={{ fontSize: '15px', fontWeight: 'bold' }}>
                {pendingCarLabel}
              </div>
            </div>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 0',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
            <span style={{ fontSize: '14px' }}>お申し込み</span>
            <span
              style={{
                marginLeft: 'auto',
                fontSize: '12px',
                color: '#4CAF50',
                fontWeight: 'bold',
              }}
            >
              完了
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 0',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            {hasPaymentDone ? (
              <>
                <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
                <span style={{ fontSize: '14px' }}>お支払い</span>
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: '12px',
                    color: '#4CAF50',
                    fontWeight: 'bold',
                  }}
                >
                  完了
                </span>
              </>
            ) : (
              <>
                <span style={{ color: '#FF9800', fontSize: '16px' }}>○</span>
                <span style={{ fontSize: '14px' }}>お支払い</span>
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: '11px',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    backgroundColor: '#FFF3E0',
                    color: '#E65100',
                  }}
                >
                  未完了
                </span>
              </>
            )}
          </div>
          {/* 車両の本登録: 既存車フローでは紐付け済みのため非表示 */}
          {!tStockCarId && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 0',
              }}
            >
              {hasRegisteredCar ? (
                <>
                  <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
                  <span style={{ fontSize: '14px' }}>車両の本登録</span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: '12px',
                      color: '#4CAF50',
                      fontWeight: 'bold',
                    }}
                  >
                    完了
                  </span>
                </>
              ) : (
                <>
                  <span style={{ color: '#FF9800', fontSize: '16px' }}>○</span>
                  <span style={{ fontSize: '14px' }}>車両の本登録</span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      backgroundColor: '#FFF3E0',
                      color: '#E65100',
                    }}
                  >
                    未完了
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {/* 決済へ: effectiveSubId があれば遷移可能。PaymentPage 側で status を取得するため
            ここでは statusLoaded / statusError に依存しない（status 一時失敗で導線を塞がない設計判断） */}
        {!!effectiveSubId && !hasPaymentDone && (
          <button
            onClick={handleClickPayment}
            style={{
              width: '100%',
              padding: '16px',
              marginBottom: '12px',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#fff',
              backgroundColor: '#1E55E6',
              border: 'none',
              borderRadius: '28px',
              cursor: 'pointer',
            }}
          >
            お支払いへ進む
          </button>
        )}

        {/* 車両選択: 所有車両がある場合、紐付ける車両を選択 */}
        {showCarSelect && (
          <div style={{ marginBottom: '16px', textAlign: 'left' }}>
            <div
              style={{
                fontSize: '14px',
                fontWeight: 'bold',
                marginBottom: '8px',
              }}
            >
              サービスに登録する車両を選択してください
            </div>
            {selectableCars.map((car) => (
              <div
                key={car.t_stock_car_id || car.m_car_id}
                onClick={() => handleLinkCar(car)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  marginBottom: '8px',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  backgroundColor: '#fff',
                  cursor: isLinking ? 'wait' : 'pointer',
                  opacity: isLinking ? 0.6 : 1,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '15px', fontWeight: 'bold' }}>
                    {car.maker_nm} {car.car_nm}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#666',
                      marginTop: '2px',
                    }}
                  >
                    {car.class_no} {car.hiragana} {car.registry_no}
                  </div>
                </div>
                <div style={{ color: '#1E55E6', fontSize: '13px' }}>選択</div>
              </div>
            ))}
          </div>
        )}

        {/* auto-link 失敗時の回復 UI */}
        {!hasRegisteredCar && autoLinkFailed && hasPaymentDone && (
          <>
            <p
              style={{
                fontSize: '13px',
                color: '#E65100',
                backgroundColor: '#FFF3E0',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '12px',
                textAlign: 'left',
              }}
            >
              車両の自動登録に失敗しました
            </p>
            <button
              onClick={() => {
                setAutoLinkFailed(false);
                setAutoLinkAttempted(false);
              }}
              style={{
                width: '100%',
                padding: '16px',
                marginBottom: '12px',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#fff',
                backgroundColor: '#1E55E6',
                border: 'none',
                borderRadius: '28px',
                cursor: 'pointer',
              }}
            >
              再試行
            </button>
          </>
        )}

        {/* 車検証を登録する: 支払い完了前 or 既存車フロー(tStockCarId有かつ未失敗)では非表示 */}
        {!hasRegisteredCar &&
          (!tStockCarId || autoLinkFailed) &&
          hasPaymentDone && (
            <button
              onClick={() => {
                window.localStorage.removeItem('subscription_t_stock_car_id');
                setTStockCarId(undefined);
                setAutoLinkFailed(false);
                const affiliateId = (store.state as any).customerInfo
                  ?.m_affiliate_id;
                storeDispatch('setRegisterUser', 'registerUser', {
                  ...initialState.registerUser,
                  affiliate_id: Number(affiliateId),
                  currentStep: 3,
                });
                f7.view.main.router.navigate(paths.registerNewMember, {
                  props: {
                    sourcePage: paths.subscriptionComplete,
                    startStep: 3,
                  },
                });
              }}
              style={{
                width: '100%',
                padding: '16px',
                marginBottom: '12px',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#1E55E6',
                backgroundColor: '#fff',
                border: '1px solid #1E55E6',
                borderRadius: '28px',
                cursor: 'pointer',
              }}
            >
              車検証を登録する
            </button>
          )}

        {statusLoaded && statusError && (
          <div
            style={{
              padding: '16px',
              marginBottom: '12px',
              fontSize: '14px',
              color: '#E65100',
              backgroundColor: '#FFF3E0',
              borderRadius: '8px',
            }}
          >
            契約情報の取得に失敗しました。ページを再読み込みしてください。
          </div>
        )}

        <button
          onClick={handleClickHome}
          style={{
            width: '100%',
            padding: '16px',
            fontSize: '14px',
            color: hasPaymentDone && hasRegisteredCar ? '#fff' : '#666',
            backgroundColor:
              hasPaymentDone && hasRegisteredCar ? '#1E55E6' : '#fff',
            border:
              hasPaymentDone && hasRegisteredCar ? 'none' : '1px solid #ccc',
            borderRadius: '28px',
            cursor: 'pointer',
          }}
        >
          {hasPaymentDone && hasRegisteredCar
            ? 'ホームに戻る'
            : 'あとで手続きする'}
        </button>
      </div>
    </PageTemplate>
  );
};
